import katex from "katex";
import TextField from "@material-ui/core/TextField";
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Tooltip,
  Divider,
  Chip,
  Tab,
  Tabs,
  Box,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { ScatterBoxLoader } from "react-awesome-loaders";
import ReplayIcon from "@material-ui/icons/Replay";
import RefreshIcon from "@material-ui/icons/Refresh";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import CommentIcon from "@material-ui/icons/Comment";
import NotificationContext from "views/Components/Context.js";
import Edit from "@material-ui/icons/Edit";
import Button from "components/CustomButtons/Button.js";
import {
  CallMade,
  CropLandscapeOutlined,
  DoneAll,
  FileCopy,
  Link,
  RotateLeft,
  SpeakerNotes,
  Visibility,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import StorageIcon from "@material-ui/icons/Storage";
import AllInboxIcon from "@material-ui/icons/AllInbox";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";

import CloseIcon from "@material-ui/icons/Close";
import CreateIssueForm from "./Lab/CreateIssueForm";
import {
  fetchPDFAPI,
  createGitHubIssueAPI,
  airflowlogsAPI,
  getIssuesAPI,
  submitLabForGenerationAPI,
  fetchNotebookAPI,
  fetchMarkdownAPI,
  saveToGitHubAPI,
  regenerateDocumentationAPI,
} from "views/APIs/APIUtility";
import MDEditor from "@uiw/react-md-editor";

import { _mapResourceToAppType } from "./CommonFunctions";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import WritingTable from "./Writing/WritingTable";

import { pdfjs, Document, Page } from "react-pdf";
import PDFViewer from "views/Components/PDFViewer";
import DeliverableTiles from "components/Tiles/DeliverableTiles";
import { setSyntheticLeadingComments } from "typescript";
import mathWrappedComponents from "views/Components/MathWrapped";
import { publishLabAPI } from "../APIs/LabsUtility";
import MarkdownEditorWithPreview from "../../components/MarkdownEditor/MarkdownEditorWithPreview";
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const options = {
  cMapUrl: "/cmaps/",
  standardFontDataUrl: "/standard_fonts/",
};

const useStyles = makeStyles({
  ...styles,
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginTop: "20px",
    boxSizing: "border-box",
  },
  title: { marginBottom: "20px" },
  button: {
    marginBottom: "20px",
    padding: "15px 30px",
    fontSize: "1.2rem",
  },
  tableContainer: {
    width: "100%",
    maxHeight: "70vh",
    margin: "0 auto",
  },
  dialogContent: {
    width: "500px",
    maxWidth: "90vw",
  },
  dialogButtonSpacing: { margin: "10px 0" },
  urlCell: {
    wordBreak: "break-word",
    maxWidth: "200px",
    textAlign: "center",
  },
  centeredCell: {
    textAlign: "center",
    padding: "16px", // Ensures equal padding inside each cell
  },
  rightAlignedTitleCell: {
    textAlign: "right",
    padding: "16px", // Ensures equal padding inside each cell
  },
  rightAlignedCell: {
    textAlign: "right",
    padding: "16px", // Ensures equal padding inside each cell
  },
  addResourceRow: {
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f5f5f5",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
});

function NotebookViewer({ html }) {
  const box = useRef(null);

  /* load MathJax exactly once */
  useEffect(() => {
    if (window.MathJax) return; // already loaded

    const s = document.createElement("script");
    s.id = "mathjax";
    s.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
    s.async = true;
    s.onload = () => {
      if (box.current) window.MathJax.typesetPromise([box.current]);
    };
    document.head.appendChild(s);
  }, []);

  /* run MathJax every time the HTML changes */
  useEffect(() => {
    if (window.MathJax && box.current) {
      window.MathJax.typesetPromise([box.current]);
    }
  }, [html]);

  return (
    <div
      ref={box}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ width: "100%", overflow: "auto" }}
    />
  );
}

export default function ReviewLabProject({
  projectID,
  projectType,
  labDetails,
  setActiveStep,
  deliverables = false,
  lab,
  setLab,
  labGeneration,
}) {
  const category = localStorage.getItem("userCategory");
  const classes = useStyles();
  const { showNotification, setNotification } = React.useContext(
    NotificationContext
  );
  const [issueForm, setIssueForm] = useState(false);
  const [issues, setIssues] = useState([]);
  const [issueObject, setIssueObject] = React.useState({
    title: "",
    description: `<!-- For Feature Requests / Enhancements -->
          ### User Story
          As a (type of user), I want (some goal) so that (some reason)

          ### Description
          What’s it all about?

          <!-- For Bugs -->
          ### What should happen:
          (Describe what you expect to happen here)

          ### What happened instead:
          (Describe what happened instead)

          ### How to reproduce this bug:
          (Provide steps to reproduce the bug)
    `,
    uploadedFiles: [],
    labels: [],
  });

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const data = await getIssuesAPI(projectID);
        setIssues(data);
      } catch (error) {
        showNotification("Error fetching issues", "danger");
      }
    };
    fetchIssues();
  }, []);

  const initialFiles = lab?.raw_resources?.map(_mapResourceToAppType) || [];
  const initialTemplate = lab?.selected_idea || null;
  const notebookSpecURL = lab?.notebook_specifications_pdf || null;
  const streamlitSpecURL = lab?.streamlit_specifications_pdf || null;
  // const initialTab = (category === "super_admin" || category === "admin")
  //   ? (labGeneration === true ? 1 : 2)
  //   : 0;

  const initialTab = 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [files, setFiles] = useState(initialFiles);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);
  const [labSpec, setLabSpec] = useState(notebookSpecURL);
  const [streamlitSpec, setStreamlitSpec] = useState(streamlitSpecURL);
  const [airflowLogs, setAirflowLogs] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [colabContent, setColabContent] = useState(null);
  const [documentationContent, setDocumentationContent] = useState("");
  const [userGuideContent, setUserGuideContent] = useState("");
  const [currentEditFile, setCurrentEditFile] = useState("");
  const [fetchMarkdownLoading, setFetchMarkdownLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);
  const [attachCode, setAttachCode] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchMarkdownAPI(lab._id);
        setDocumentationContent(data.documentation);
        setUserGuideContent(data.user_guide);
        setFetchMarkdownLoading(false);
      } catch (err) {
        console.error("Error fetching markdown:", err);
      }
    };

    if (lab?._id) {
      fetchData();
    }
  }, [lab, labDetails]);

  function getDefaultWidth() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 600) return 320; // Mobile
    if (screenWidth < 960) return 500; // Tablet
    if (screenWidth < 1280) return 700; // Laptop
    return 950; // Desktop
  }

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(getDefaultWidth()); // Default width
  const [pdfFile, setPdfFile] = useState(null);
  const [streamlitPdfFile, setStreamlitPdfFile] = useState(null);

  useEffect(() => {
    async function fetchPDF(url) {
      try {
        if (url === undefined || url === null || url === "") {
          return;
        }
        const formData = new FormData();
        formData.append("url", url);
        const response = await fetchPDFAPI(formData); // Convert the response to a Blob
        const pdfBlob = await response.blob(); // Create a URL for the fetched PDF Blob
        const pdfBlobUrl = URL.createObjectURL(pdfBlob); // Set the Blob URL to your state to display in the viewer
        if (url === notebookSpecURL) {
          setPdfFile(pdfBlobUrl);
        } else if (url === streamlitSpecURL) {
          setStreamlitPdfFile(pdfBlobUrl);
        }
      } catch (error) {
      } finally {
      }
    }
    fetchPDF(notebookSpecURL);

    fetchPDF(streamlitSpecURL);
    async function fetchNotebook() {
      const data = await fetchNotebookAPI(lab["_id"]);
      setColabContent(data.colab_content);
    }
    fetchNotebook();
  }, [notebookSpecURL, streamlitSpecURL]);

  useEffect(() => {
    async function airflowlogs() {
      try {
        setLogsLoading(true);
        const formData = new FormData();
        formData.append("project_id", projectID);
        formData.append("project_type", "lab");

        const response = await airflowlogsAPI(formData);
        if (response.error) return;

        setAirflowLogs(response);
        setSyntheticLeadingComments(response);
      } catch (error) {
        console.error("Error fetching Airflow logs:", error);
      } finally {
        setLogsLoading(false);
      }
    }

    // Call the async function conditionally
    if (category === "super_admin" || category === "admin") {
      airflowlogs();
    }
  }, [refresh]); // Add dependencies here if projectID/category can change

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSetFileResources = (resources) => {
    const mappedResources = resources.map(_mapResourceToAppType);
    setFiles(mappedResources);
  };

  const linkBlueprints = [
    {
      key: "lab_url",
      name: "Streamlit Application",
      description: "Hosted Streamlit application for the lab.",
    },
    {
      key: "repo_url",
      name: "GitHub Repository",
      description: "Source code repository for the lab.",
    },
    {
      key: "documentation_url",
      name: "Documentation Codelab",
      description: "Step-by-step documentation for the lab.",
    },
    {
      key: "user_guide_url",
      name: "User Guide Codelab",
      description: "User guide for the lab.",
    },
    {
      key: "colab_url",
      name: "Notebook",
      description: "Jupyter notebook for the lab.",
    },
  ];

  // 2. Build the array only for links that are non-empty
  const links = useMemo(
    () =>
      linkBlueprints
        .filter(({ key }) => labDetails[key]) // keep only valid URLs
        .map(({ key, name, description }) => {
          return {
            name,
            description,
            url: labDetails[key],
            viewAction: () => window.open(labDetails[key], "_blank"),
          };
        }),
    [labDetails]
  );

  const ReviewLabProjectSubmitHandler = () => {
    if (deliverables) {
      showNotification(
        "Your project has been marked for publishing.",
        "success"
      );
    } else {
      setActiveStep(4);
    }
  };

  const addFeedback = async () => {
    showNotification(
      "Your feedback has been saved and will be taken into consideration.",
      "success"
    );
  };

  const newIssue = async () => {
    setIssueForm(true);
  };

  const handleClose = () => {
    setIssueForm(false);
    setIssueObject({
      title: "",
      description: `
    <!-- For Feature Requests / Enhancements -->
    ### User Story
    As a (type of user), I want (some goal) so that (some reason)

    ### Description
    What’s it all about?

    <!-- For Bugs -->
    ### What should happen:
    (Describe what you expect to happen here)

    ### What happened instead:
    (Describe what happened instead)

    ### How to reproduce this bug:
    (Provide steps to reproduce the bug)
  `,
      uploadedFiles: [],
      labels: [],
    });
  };

  const createNewIssue = async () => {
    try {
      const formData = new FormData();
      formData.append("lab_id", projectID);
      formData.append("issue_title", issueObject.title);
      formData.append("issue_description", issueObject.description);
      formData.append("labels", issueObject.labels);
      issueObject.uploadedFiles.forEach((file) => {
        formData.append("uploaded_files", file);
      });

      // API call to create a new issue
      const data = await createGitHubIssueAPI(formData);
      showNotification("Issue created successfully", "success");
      setIssues([
        ...issues,
        {
          title: issueObject.title,
          description: issueObject.description,
          state: "open",
          labels: issueObject.labels,
        },
      ]);
    } catch (error) {
      showNotification("Error creating issue", "danger");
    } finally {
      handleClose();
    }
  };
  const [numPages, setNumPages] = useState(null);
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);
  const [pdfLoading, setPdfLoading] = useState({
    isLoading: false,
    message: "",
  });
  const [modelDetails, setModelDetails] = useState({
    company: "",
    model: "",
    apiKey: "",
    name: "",
    description: "",
  });

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    setTimeout(() => {
      updateWidth(); // Set width after timeout
      setPdfLoading({ isLoading: false, message: "" });
    }, 2000); // Wait for 2 seconds before setting loading to false

    window.addEventListener("resize", updateWidth);

    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const [selectedTabs, setSelectedTabs] = useState({});
  const handleChange = (runId, newIndex) => {
    setSelectedTabs((prev) => ({ ...prev, [runId]: newIndex }));
  };

  const formatTaskName = (taskId) =>
    taskId.replace(/[_\-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatRunId = (runId) => {
    const timestamp = runId.split("__")[1];
    const date = new Date(timestamp);
    return (
      new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "UTC",
      }).format(date) + " UTC"
    );
  };

  const isStepSuccess = (logText) => {
    // If the log contains the success message, return success
    if (logText.includes("$$$step completed successfully$$$")) {
      return "success";
    }

    // If the log contains failure or error, return failure
    if (
      logText.includes("$$$step failed$$$") ||
      labDetails.status === "Lab Generation Failed"
    ) {
      return "failure";
    }

    // If no message or log is empty, assume it's still running
    return "running";
  };

  // Edit markdown modal
  const [editOpen, setEditOpen] = useState(false);
  const [editContent, setEditContent] = useState("lorem impsum");

  const openEdit = (link) => {
    if (link == "Documentation Codelab") {
      setEditContent(documentationContent);
      setCurrentEditFile("documentation.md");
    } else if (link == "User Guide Codelab") {
      setEditContent(userGuideContent);
      setCurrentEditFile("user_guide.md");
    }
    setEditOpen(true);
  };
  const handleEditClose = () => {
    setFeedback("");
    setEditOpen(false);
  };
  const handleEditSave = async () => {
    try {
      const formData = new FormData();
      formData.append("lab_id", lab._id);
      formData.append("file_path", currentEditFile);
      formData.append("content", editContent);
      formData.append("commit_message", "Update");

      const result = await saveToGitHubAPI(formData);

      handleEditClose();
    } catch (err) {
      console.error("Error saving to GitHub:", err);
    }
  };
  const handleRegenerate = async () => {
    try {
      setRegenLoading(true);
      const result = await regenerateDocumentationAPI(
        lab._id,
        currentEditFile,
        feedback,
        editContent,
        attachCode
      );
      setEditContent(result);
    } catch (err) {
      console.error(err);
    } finally {
      setRegenLoading(false);
    }
  };

  const submitHandler = async (saveAPIKEY = false) => {
    // handleReviewDialogClose();
    // setLoadingState({ isLoading: true, message: "Saving Technical Specifications..." });
    const formData = new FormData();
    formData.append("lab_id", projectID);
    formData.append("company", modelDetails.company);
    formData.append("model", modelDetails.model);
    formData.append("key", modelDetails.apiKey);
    formData.append("name", modelDetails.name);
    formData.append("description", modelDetails.description);
    formData.append("type", "llm");
    formData.append("saveAPIKEY", false);

    await submitLabForGenerationAPI(formData);
    window.location.reload();
  };


  const [loading, setLoading] = useState(false);

  const publishLab = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("lab_id", lab._id);
      const result = await publishLabAPI(formData);
      console.log("Lab published successfully:", result);
      showNotification("Lab published successfully", "success");
      setLab({ ...lab, status: "Published" });
    } catch (error) {
      showNotification("Error publishing lab", "danger");
      console.error("Error publishing lab:", error);
    } finally {
      setLoading(false);
    }
  };

  function renderContent() {
    switch (activeTab) {
      case 0:
        return (
          <Grid container spacing={4}>
            {/* Uploaded Resources Section */}
            <Grid item xs={12}>
              <Typography variant="h5" style={{ marginTop: "10px" }}>
                Uploaded resources
              </Typography>
              <Typography variant="body1">
                Uploaded source files for the lab.
              </Typography>
              <Divider style={{ marginBottom: "20px", marginTop: "10px" }} />
              <WritingTable
                files={files}
                selectColumnRequired={false}
                deleteActionRequired={false}
                uploadActionRequired={false}
              />
            </Grid>

            {/* Idea & PDF Viewer */}
            <Grid item xs={12}>
              <Grid container spacing={2} style={{ alignItems: "stretch" }}>
                <Grid item xs={12} md={6}>
                  <div style={{ height: "100%" }}>
                    <Typography variant="h5">Idea Selected</Typography>
                    <Typography
                      variant="body1"
                      style={{ marginBottom: "10px" }}
                    >
                      Chosen idea for lab creation.
                    </Typography>
                    <Divider style={{ marginBottom: "20px" }} />
                    <div data-color-mode="light">
                      <MarkdownEditorWithPreview
                        disabled={true}
                        value={selectedTemplate.description}
                        hideToolbar={true}
                        height={600}
                      />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={12} md={6} ref={containerRef}>
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h5">Notebook Specification</Typography>
                    <Typography
                      variant="body1"
                      style={{ marginBottom: "10px" }}
                    >
                      Notebook Specification for the project.
                    </Typography>
                    <Divider style={{ marginBottom: "20px" }} />
                    {pdfFile && (
                      <div
                        style={{
                          overflowY: "auto",
                          overflowX: "hidden",
                          flexGrow: 1,
                          width: "100%",
                          maxHeight: "600px",
                        }}
                      >
                        <Document
                          file={pdfFile}
                          onLoadSuccess={onDocumentLoadSuccess}
                          options={options}
                        >
                          {Array.from(new Array(numPages), (el, index) => (
                            <Page
                              key={`page_${index + 1}`}
                              pageNumber={index + 1}
                              width={containerWidth}
                              style={{ margin: "10px 0" }}
                            />
                          ))}
                        </Document>
                      </div>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={6} ref={containerRef}>
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h5">
                      Streamlit Specifications
                    </Typography>
                    <Typography
                      variant="body1"
                      style={{ marginBottom: "10px" }}
                    >
                      Streamlit Specifications for the project.
                    </Typography>
                    <Divider style={{ marginBottom: "20px" }} />
                    {streamlitPdfFile && (
                      <div
                        style={{
                          overflowY: "auto",
                          overflowX: "hidden",
                          flexGrow: 1,
                          width: "100%",
                          maxHeight: "600px",
                        }}
                      >
                        <Document
                          file={streamlitPdfFile}
                          onLoadSuccess={onDocumentLoadSuccess}
                          options={options}
                        >
                          {Array.from(new Array(numPages), (el, index) => (
                            <Page
                              key={`page_${index + 1}`}
                              pageNumber={index + 1}
                              width={containerWidth}
                              style={{ margin: "10px 0" }}
                            />
                          ))}
                        </Document>
                      </div>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={6} ref={containerRef}>
                  {colabContent && (
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="h5">Notebook</Typography>
                      <Typography
                        variant="body1"
                        style={{ marginBottom: "10px" }}
                      >
                        Notebook for the project.
                      </Typography>
                      <Divider style={{ marginBottom: "20px" }} />
                      {colabContent && (
                        <div
                          style={{
                            overflowY: "auto",
                            overflowX: "hidden",
                            flexGrow: 1,
                            width: "100%",
                            maxHeight: "600px",
                          }}
                        >
                          <NotebookViewer html={colabContent} />
                        </div>
                      )}
                    </div>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <>
            {/* {!deliverables ? (
              <Grid item xs={12} sm={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
                <Typography variant="h5">Review Lab</Typography>
                <Typography variant="body1">
                  Review the generated code lab & application links for the project.
                </Typography>
                <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
                  <Typography variant="h5">Final Deliverables</Typography>
                  <Typography variant="body1">
                    Review the final deliverables for the project.
                  </Typography>
                </Grid>
                <Divider />
              </>
            )} */}
            {labGeneration ? (
              <>
                <Typography variant="h5">Review Lab</Typography>
                <Typography variant="body1">
                  Review the generated code lab & application links for the
                  project.
                </Typography>
                <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />

                {/* Source Code / Links Table */}
                <Grid item xs={12} sm={12}>
                  {links.length > 0 && (
                    <TableContainer
                      component={Paper}
                      className={classes.tableContainer}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Name</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Description</strong>
                            </TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {links.map((link, index) => (
                            <TableRow key={index}>
                              <TableCell className={classes.tableCell}>
                                <Tooltip title={link.name}>
                                  <span>{link.name}</span>
                                </Tooltip>
                              </TableCell>

                              <TableCell className={classes.tableCell}>
                                <Tooltip title={link.description}>
                                  <span>{link.description}</span>
                                </Tooltip>
                              </TableCell>

                              <TableCell className={classes.tableCell}>
                                <Tooltip title="Copy Link">
                                  <IconButton
                                    onClick={() => {
                                      navigator.clipboard.writeText(link.url);
                                      showNotification(
                                        "Link copied to clipboard",
                                        "success"
                                      );
                                    }}
                                  >
                                    <FileCopy />
                                  </IconButton>
                                </Tooltip>

                                {link.name.includes("Codelab") ? (
                                  <Tooltip
                                    title={
                                      fetchMarkdownLoading
                                        ? "Loading..."
                                        : "Edit"
                                    }
                                  >
                                    <IconButton
                                      disabled={fetchMarkdownLoading}
                                      onClick={() => {
                                        openEdit(link.name);
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Provide Feedback">
                                    <IconButton onClick={newIssue}>
                                      <SpeakerNotes />
                                    </IconButton>
                                  </Tooltip>
                                )}

                                <Tooltip title="View Link">
                                  <IconButton onClick={link.viewAction}>
                                    <CallMade />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Grid>

                {/* Previous Feedback */}
                <Grid
                  item
                  xs={12}
                  sm={12}
                  style={{ marginTop: "20px", marginBottom: "10px" }}
                >
                  <Divider />
                  <Typography variant="h5">Previous Feedback</Typography>
                  <Typography variant="body1">
                    You can check your previous feedback and issues here.
                  </Typography>
                  <Divider
                    style={{ marginTop: "10px", marginBottom: "10px" }}
                  />

                  <TableContainer
                    component={Paper}
                    className={classes.tableContainer}
                    style={{ marginTop: "10px" }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Name</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Labels</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Status</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {issues.length > 0 ? (
                          issues.map((issue, index) => (
                            <TableRow key={index}>
                              <TableCell>{issue.title}</TableCell>
                              <TableCell width="50%">
                                <div data-color-mode="light">
                                  <MarkdownEditorWithPreview
                                    source={issue.description}
                                    disabled={true}
                                    height={100}
                                    hideToolbar
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                {issue.labels?.map((label, idx) => (
                                  <Chip
                                    key={idx}
                                    label={label}
                                    style={{ margin: "2px" }}
                                    color="QCreateColor"
                                  />
                                ))}
                              </TableCell>
                              <TableCell>
                                {issue.state === "open" ? "Open" : "Closed"}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No Open Issues/Bugs found.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Submit Button */}
                {deliverables && (
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    style={{ marginTop: "10px", marginBottom: "10px" }}
                  >
                    <Button
                      color="quCreateColor"
                      onClick={ReviewLabProjectSubmitHandler}
                      style={{ float: "right" }}
                      startIcon={<DoneAll />}
                    >
                      Publish
                    </Button>
                  </Grid>
                )}
              </>
            ) : labDetails.status === "Lab Generation Failed" ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                minHeight="50vh"
                textAlign="center"
              >
                <img
                  src="https://cdn-icons-png.flaticon.com/512/463/463612.png"
                  alt="Failure Icon"
                  style={{ width: 80, height: 80, marginBottom: 16 }}
                />
                <Typography
                  variant="h6"
                  gutterBottom
                  color="error"
                  style={{ textTransform: "none" }}
                >
                  We were unable to generate your lab.
                </Typography>
                <Typography
                  variant="body1"
                  style={{ maxWidth: 400, marginBottom: 24 }}
                >
                  Something went wrong during the lab generation process. Please
                  try again or contact the administrator at{" "}
                  <a href="mailto:info@qusandbox.com">info@qusandbox.com</a> for
                  support.
                </Typography>
                <Button
                  variant="contained"
                  color="quCreateColor"
                  onClick={submitHandler}
                  startIcon={<ReplayIcon />}
                >
                  Regenerate Lab
                </Button>
              </Box>
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
              >
                <CircularProgress
                  color="quCreateColor"
                  className={classes.loaderClass}
                />
                <Typography
                  variant="h6"
                  style={{ marginLeft: 16, textTransform: "none" }}
                >
                  Your lab is being generated...
                </Typography>
              </Box>
            )}
          </>
        );

      case 2:
        if (airflowLogs.length === 0) {
          return (
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                style={{ textTransform: "none" }}
                gutterBottom
              >
                No logs found.
              </Typography>
            </Box>
          );
        }
        return (
          <>
            {Object.entries(airflowLogs).map(([runId, taskLogs]) => {
              const taskIds = Object.keys(taskLogs);
              const selectedTab = selectedTabs[runId] || 0;

              return logsLoading ? (
                <Paper style={{ padding: 24 }}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="60vh"
                  >
                    <CircularProgress
                      color="quCreateColor"
                      className={classes.loaderClass}
                    />
                    <Typography
                      variant="h6"
                      style={{ marginLeft: 16, textTransform: "none" }}
                    >
                      Fetching logs...
                    </Typography>
                  </Box>
                </Paper>
              ) : (
                <Paper style={{ padding: 24 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    paddingBottom={2}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      style={{ textTransform: "none" }}
                    >
                      {`Last Run Dated: ${formatRunId(runId)}`}
                    </Typography>
                    <Button
                      startIcon={<RefreshIcon />}
                      color="quCreateColor"
                      onClick={() => {
                        setRefresh(!refresh);
                        setLogsLoading(true);
                      }}
                    >
                      Refresh
                    </Button>
                  </Box>

                  <Grid
                    container
                    spacing={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {/* Tabs Panel */}
                    <Grid item xs={2}>
                      <Grid
                        container
                        direction="column"
                        justifyContent="center"
                      >
                        <Grid item>
                          <Tabs
                            orientation="vertical"
                            variant="scrollable"
                            value={selectedTab}
                            onChange={(e, newVal) =>
                              handleChange(runId, newVal)
                            }
                            style={{ borderRight: "1px solid #ddd" }}
                            TabIndicatorProps={{
                              style: {
                                backgroundColor: quCreateColor,
                                background: quCreateColor,
                              },
                            }}
                          >
                            {taskIds.map((taskId, index) => {
                              const response = isStepSuccess(taskLogs[taskId]);
                              return (
                                <Tab
                                  key={taskId}
                                  label={
                                    <Box display="flex" alignItems="center">
                                      <Typography variant="body2">
                                        Step {index + 1}
                                      </Typography>
                                      <Box ml={1}>
                                        {response === "success" ? (
                                          <CheckCircleIcon
                                            fontSize="small"
                                            style={{ color: "green" }}
                                          />
                                        ) : response === "failure" ? (
                                          <CancelIcon
                                            fontSize="small"
                                            style={{ color: "red" }}
                                          />
                                        ) : (
                                          <CircularProgress
                                            size={20}
                                            className={classes.loaderClass}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  }
                                />
                              );
                            })}
                          </Tabs>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Tab Content */}
                    <Grid item xs={10}>
                      {taskIds.map((taskId, index) => (
                        <div
                          key={taskId}
                          hidden={selectedTab !== index}
                          role="tabpanel"
                          id={`tabpanel-${index}`}
                          aria-labelledby={`tab-${index}`}
                        >
                          {selectedTab === index && (
                            <Box p={2}>
                              <Typography variant="subtitle1" gutterBottom>
                                Step {index + 1}: {formatTaskName(taskId)}
                              </Typography>
                              <Paper
                                variant="outlined"
                                style={{
                                  maxHeight: 300,
                                  overflowY: "auto",
                                  padding: 12,
                                  backgroundColor: "#f9f9f9",
                                }}
                              >
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    fontSize: 13,
                                  }}
                                >
                                  {taskLogs[taskId]}
                                </pre>
                              </Paper>
                            </Box>
                          )}
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </>
        );

      default:
        return null;
    }
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          Publishing lab...
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Typography variant="h5">Lab Review</Typography>
            <Typography variant="body1" style={{ marginBottom: "10px" }}>
              Review the final deliverables for the lab.
            </Typography>
          </div>
          <Button
            color="quCreateColor"
            onClick={publishLab}
            startIcon={<DoneAll />}
          >
            Publish Lab {lab.status === "Published" ? "Again" : ""}
          </Button>
        </div>
        <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />
        <DeliverableTiles
          active={activeTab}
          color="warning"
          alignCenter
          tabs={[
            {
              heading: "Summary of all steps",
              icon: StorageIcon,
              description: "Includes a summary of all the steps of the lab.",
              onClick: () => setActiveTab(0),
            },
            {
              heading: "Final Deliverables",
              icon: AllInboxIcon,
              description:
                "Contains links to completed lab exercises and supporting content.",
              onClick: () => setActiveTab(1),
            },
          ]}
        />

        <Divider style={{ marginTop: "10px", marginBottom: "10px" }} />

        {/* <Tabs
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{
            style: {
              backgroundColor: quCreateColor,
              background: quCreateColor,
            }
          }}
          // indicatorColor={quCreateColor}
          centered
        >
          <Tab
            label={
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <StorageIcon fontSize="small" /> Source Files
              </span>
            }
            className={classes.customTab}
          />
          <Tab
            label={
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <AllInboxIcon fontSize="small" /> Final Deliverables
              </span>
            }
            className={classes.customTab}
          />
        </Tabs> */}
      </Grid>

      {/* Render content based on active tab */}
      <Grid item xs={12}>
        {renderContent()}
      </Grid>

      <Dialog open={issueForm} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateIssueForm
            issueObject={issueObject}
            setIssueObject={setIssueObject}
            createNewIssue={createNewIssue}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="md">
        <DialogTitle style={{ paddingRight: 48 }}>
          {"Edit Markdown"}
          <IconButton
            aria-label="close"
            onClick={handleEditClose}
            style={{ position: "absolute", right: 8, top: 8 }}
            disabled={regenLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {regenLoading && <LinearProgress />}

          <div
            data-color-mode="light"
            style={{ opacity: regenLoading ? 0.6 : 1 }}
          >
            <MarkdownEditorWithPreview
              value={editContent}
              height={600}
              onChange={setEditContent}
            />
          </div>

          <Box p={2}></Box>

          <TextField
            placeholder="Enter your feedback"
            label="Feedback"
            variant="outlined"
            rows={4}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            fullWidth
            multiline
            InputLabelProps={{
              shrink: true,
            }}
            disabled={regenLoading}
          />
        </DialogContent>

        <DialogActions style={{ justifyContent: "space-between" }}>
          {/* Left side: checkbox with text */}
          <FormControlLabel
            control={
              <Checkbox
                checked={attachCode}
                onChange={(e) => setAttachCode(e.target.checked)}
                color="primary"
              />
            }
            label="Attach Code for Regeneration"
          />

          {/* Right side: buttons */}
          <div>
            <Button onClick={handleEditClose} disabled={regenLoading}>
              Cancel
            </Button>

            <Button
              onClick={handleRegenerate}
              color="info"
              variant="outlined"
              disabled={regenLoading}
            >
              Regenerate with Feedback
            </Button>

            <Button
              color="quCreateColor"
              onClick={handleEditSave}
              disabled={regenLoading}
            >
              Save and Publish
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
